export const VARIANTS_PER_PAGE = 5;

export const variantDisplay = (v) =>
  v?.variant ?? v?.prefix ?? (typeof v === "string" ? v : "");

export const partMainId = (p) => {
  if (!p) return "";
  const mid = p.mainPartId?._id || p.mainPartId;
  return mid ? String(mid) : String(p._id);
};

export const partOutputMain = (p) => {
  if (!p) return null;
  if (p.mainPartId && typeof p.mainPartId === "object") return p.mainPartId;
  if (!p.mainPartId) return p;
  return null;
};

export const partBaseLabel = (main) => {
  if (!main) return "—";
  return `${main.code || "—"} — ${main.name || "—"}`;
};

export const partVariantLabel = (p) => {
  const v = variantDisplay(p?.variantId);
  if (v) return v;
  return p?.code && p?.name ? `${p.name}` : "—";
};

export const partInputLabel = (p) => {
  const main = partOutputMain(p);
  const base = main ? partBaseLabel(main) : partBaseLabel(p);
  const v = partVariantLabel(p);
  return v && v !== "—" && main ? `${base} (${v})` : `${p?.name || "—"}`;
};

/** Unit symbol or name from populated unitId (or nested mainPartId.unitId). */
export const partUnitLabel = (p) => {
  const u = p?.unitId;
  if (!u) return "";
  if (typeof u === "object") {
    return String(u.symbol || "").trim() || String(u.name || "").trim();
  }
  return "";
};

export const partUnitLabelForDoc = (doc, mainPartById) => {
  let label = partUnitLabel(doc);
  if (label) return label;
  const nestedMain = doc?.mainPartId;
  if (nestedMain && typeof nestedMain === "object") {
    label = partUnitLabel(nestedMain);
    if (label) return label;
  }
  if (!doc?.mainPartId || !mainPartById) return "";
  const mid = String(doc.mainPartId?._id || doc.mainPartId || "");
  return partUnitLabel(mainPartById(mid));
};

export const quantityWithUnit = (qty, unit) => {
  const q = String(qty ?? "").trim();
  if (!q) return "—";
  return unit ? `${q} ${unit}` : q;
};

/** Karşılık birimi sembolü (hammadde). */
export const partEquivalentUnitSymbol = (doc) => {
  const u = doc?.equivalentUnitId;
  if (!u || typeof u !== "object") return "";
  return String(u.symbol || "").trim() || String(u.name || "").trim();
};

/** Parçalar tablosundaki Birim (unitId) sembolü. */
export const partPrimaryUnitSymbol = (doc) => {
  const u = doc?.unitId;
  if (!u || typeof u !== "object") return "";
  return String(u.symbol || "").trim() || String(u.name || "").trim();
};

/**
 * Fire için birim: Parçalar kaydındaki karşılık birimi (hammadde) veya Birim (unitId).
 */
export const partAssignedUnitSymbol = (doc, mainPartById) => {
  if (!doc) return "";
  const eq = partEquivalentPerUnit(doc, mainPartById);
  if (eq?.unitSymbol) return String(eq.unitSymbol).trim();
  const primary = partPrimaryUnitSymbol(doc);
  if (primary) return primary;
  if (doc.mainPartId) {
    const main =
      typeof doc.mainPartId === "object" && doc.mainPartId !== null
        ? doc.mainPartId
        : mainPartById?.(String(doc.mainPartId?._id || doc.mainPartId || ""));
    if (main) {
      const eqMain = partEquivalentPerUnit(main, mainPartById);
      if (eqMain?.unitSymbol) return String(eqMain.unitSymbol).trim();
      const mainPrimary = partPrimaryUnitSymbol(main);
      if (mainPrimary) return mainPrimary;
    }
  }
  return "";
};

/** Girdi satırı fire birimi (Parçalar tablosundan). */
export const inputMaterialMetricUnit = (doc, mainPartById, storedUnit = "") => {
  const saved = String(storedUnit || "").trim();
  if (saved) return saved;
  return partAssignedUnitSymbol(doc, mainPartById);
};

export const partEquivalentPerUnit = (doc, mainPartById) => {
  if (!doc) return null;
  let target = doc;
  if (!doc.isRawMaterial && doc.mainPartId) {
    if (typeof doc.mainPartId === "object" && doc.mainPartId !== null) {
      target = doc.mainPartId;
    } else if (mainPartById) {
      const main = mainPartById(
        String(doc.mainPartId?._id || doc.mainPartId || ""),
      );
      if (main) target = main;
    }
  }
  if (!target?.isRawMaterial) return null;
  const val = Number(target.equivalentValue);
  if (!Number.isFinite(val) || val <= 0) return null;
  return {
    valuePerUnit: val,
    unitSymbol: partEquivalentUnitSymbol(target),
  };
};

const roundWeight = (n) => Math.round(n * 10000) / 10000;

/**
 * Çıktı karşılık ağırlığı:
 * Σ (girdi karşılık × kullanılan miktar) × çevrim miktarı − fire
 */
export const computeOutputEquivalentWeight = ({
  outputPartId,
  inputs = [],
  manualAssignments = {},
  catalog = [],
  mainPartById,
  cycleQuantity = 1,
  wastage = 0,
}) => {
  const outId = String(outputPartId || "");
  const materials = inputMaterialsForOutputVariant(
    outId,
    manualAssignments,
    inputs,
    catalog,
    mainPartById,
  );
  if (!materials.length) {
    return {
      ok: false,
      reason: "no_inputs",
      perOutputUnit: 0,
      gross: 0,
      net: 0,
      unitSymbol: "",
      lines: [],
    };
  }

  const lines = [];
  let perOutputUnit = 0;
  let unitSymbol = "";

  for (const m of materials) {
    const doc = partDocById(m.partId, [], catalog);
    const meta = partEquivalentPerUnit(doc, mainPartById);
    const used = Math.max(0, Number(m.consumptionRatio) || 0);
    const label = doc ? partInputLabel(doc) : String(m.partId);
    if (!meta) {
      lines.push({
        label,
        used,
        valuePerUnit: null,
        contribution: 0,
        missingEquivalent: true,
      });
      continue;
    }
    const contribution = roundWeight(meta.valuePerUnit * used);
    perOutputUnit += contribution;
    if (!unitSymbol && meta.unitSymbol) unitSymbol = meta.unitSymbol;
    lines.push({
      label,
      used,
      valuePerUnit: meta.valuePerUnit,
      unitSymbol: meta.unitSymbol,
      contribution,
      missingEquivalent: false,
    });
  }

  const hasEquivalent = lines.some((l) => !l.missingEquivalent);
  if (!hasEquivalent) {
    return {
      ok: false,
      reason: "no_equivalent",
      perOutputUnit: 0,
      gross: 0,
      net: 0,
      unitSymbol: "",
      lines,
    };
  }

  const cq = Math.max(0, Number(cycleQuantity) || 0);
  const w = Math.max(0, Number(wastage) || 0);
  const gross = roundWeight(perOutputUnit * cq);
  const net = roundWeight(Math.max(0, gross - w));

  return {
    ok: true,
    perOutputUnit: roundWeight(perOutputUnit),
    gross,
    net,
    unitSymbol,
    wastage: w,
    cycleQuantity: cq,
    lines,
  };
};

export const formatEquivalentWeight = (weight, unitSymbol) => {
  const { value, unit } = getMetricDisplayParts(weight, unitSymbol);
  return unit ? `${value} ${unit}` : value;
};

/** Paketleme BOM: Σ (girdi × miktar) − satır fire */
export const computeAssemblyPackagingWeight = (
  inputMaterials = [],
  catalog = [],
) => {
  let total = 0;
  let unitSymbol = "";
  for (const m of inputMaterials) {
    const doc = partDocById(m.partId, [], catalog);
    const meta = partEquivalentPerUnit(doc);
    const ratio = Math.max(0, Number(m.consumptionRatio) || 0);
    if (meta) {
      total += meta.valuePerUnit * ratio;
      if (!unitSymbol && meta.unitSymbol) unitSymbol = meta.unitSymbol;
    }
    total -= Math.max(0, Number(m.wastage) || 0);
  }
  if (!unitSymbol) {
    return { ok: false, net: 0, unitSymbol: "" };
  }
  return {
    ok: true,
    net: roundWeight(Math.max(0, total)),
    unitSymbol,
  };
};

/** Numeric value + unit code (kg, lt, …) for compact UI. */
export const getMetricDisplayParts = (weight, unitSymbol) => {
  if (weight == null || Number.isNaN(Number(weight))) {
    return { value: "—", unit: "" };
  }
  const n = Number(weight);
  const value = Number.isInteger(n) ? String(n) : String(n);
  const unit = String(unitSymbol || "").trim();
  return { value, unit };
};

/** Karşılık birimi: hesaplanan ağırlıktan veya atanmış girdilerden. */
/**
 * Form display: live compute first (same formula as save / inventory tree fallback).
 * Saved outputWeight only when compute is not possible (matches tree stored value).
 */
export const resolveOutputDisplayWeight = ({
  settings = {},
  weightCalc,
} = {}) => {
  const savedUnit = String(settings.weightUnit || "").trim();
  if (weightCalc?.ok) {
    return {
      net: weightCalc.net,
      unit: String(weightCalc.unitSymbol || "").trim() || savedUnit,
      source: "computed",
    };
  }
  const savedRaw = settings.outputWeight;
  const savedNum = savedRaw !== "" && savedRaw != null ? Number(savedRaw) : NaN;
  if (Number.isFinite(savedNum) && savedUnit) {
    return { net: savedNum, unit: savedUnit, source: "saved" };
  }
  return {
    net: null,
    unit: savedUnit || String(weightCalc?.unitSymbol || "").trim(),
    source: null,
  };
};

export const inferEquivalentUnitSymbol = ({
  outputPartId,
  inputs = [],
  manualAssignments = {},
  catalog = [],
  mainPartById,
  weightCalc,
} = {}) => {
  const fromCalc = String(weightCalc?.unitSymbol || "").trim();
  if (fromCalc) return fromCalc;
  const materials = inputMaterialsForOutputVariant(
    outputPartId,
    manualAssignments,
    inputs,
    catalog,
    mainPartById,
  );
  for (const m of materials) {
    const doc = partDocById(m.partId, [], catalog);
    const meta = partEquivalentPerUnit(doc, mainPartById);
    if (meta?.unitSymbol) return meta.unitSymbol;
  }
  return "";
};

export const labelWithUnit = (baseLabel, unit) => {
  const u = String(unit || "").trim();
  return u ? `${baseLabel} (${u})` : baseLabel;
};

export const variantIdOf = (p) => {
  if (!p) return "";
  return String(p.variantId?._id || p.variantId || "");
};

/** Code prefix used when variantId is missing (e.g. NF1000 from NF1000-Gövde). */
export const partCodeVariantKey = (code) => {
  const c = String(code ?? "").trim();
  if (!c) return "";
  const m = c.match(/^([A-Z]{2}\d+[A-Z]?)/i);
  return m ? m[1].toUpperCase() : "";
};

/** Stable key for matching input/output variants (id, label, then code). */
export const variantMatchKeyOf = (p) => {
  if (!p) return "";
  const oid = variantIdOf(p);
  if (oid) return `oid:${oid}`;
  const label = variantDisplay(p?.variantId);
  if (label) return `lbl:${String(label).trim().toLowerCase()}`;
  const fromCode = partCodeVariantKey(p?.code);
  if (fromCode) return `code:${fromCode}`;
  return "";
};

export const partDocById = (partId, variantItems, catalog) => {
  const id = String(partId);
  return (
    (variantItems || []).find((v) => String(v._id) === id) ||
    (catalog || []).find((p) => String(p._id) === id) ||
    null
  );
};

export const inputVariantsForMain = (mainPartId, catalog) => {
  if (!mainPartId) return [];
  const mid = String(mainPartId);
  return (catalog || []).filter(
    (p) => String(p.mainPartId?._id || p.mainPartId || "") === mid,
  );
};

/** Main part for girdi lookup (includes purchased; not limited to output main-parts API). */
export const inputMainPartFromCatalog = (id, catalog) => {
  const idStr = String(id || "");
  if (!idStr) return null;
  const main = (catalog || []).find(
    (p) => String(p._id) === idStr && isPartMainRow(p),
  );
  if (!main) return null;
  const variantCount =
    Number(main.variantCount) || inputVariantsForMain(idStr, catalog).length;
  return { ...main, variantCount };
};

export const isPartMainRow = (p) => {
  const mid = p?.mainPartId?._id ?? p?.mainPartId;
  return mid == null || mid === "";
};

export const mainPartDocToSelectOption = (p) => {
  const n = Number(p?.variantCount) || 0;
  const suffix = n > 0 ? ` (${n} varyant)` : " (varyantsız)";
  return {
    value: String(p._id),
    label: `${partBaseLabel(p)}${suffix}`,
  };
};

/** Select options for main-part dropdowns (alphabetic by label, TR locale). */
export const mainPartsToSelectOptions = (parts) =>
  [...(parts || [])]
    .map(mainPartDocToSelectOption)
    .sort((a, b) => a.label.localeCompare(b.label, "tr"));

export const ensureSelectedInSelectOptions = (
  options,
  selectedId,
  catalog,
  toOption = mainPartDocToSelectOption,
) => {
  const id = String(selectedId || "");
  if (!id || options.some((o) => o.value === id)) return options;
  const doc = (catalog || []).find((p) => String(p._id) === id);
  if (!doc) return options;
  return [...options, toOption(doc)].sort((a, b) =>
    a.label.localeCompare(b.label, "tr"),
  );
};

export const buildInputMainOptions = (row, catalog) => {
  const excludeOut = String(row.baseKey || "");
  const selectedId = String(row.inputMainPartId || "");
  const byId = new Map();

  for (const p of catalog || []) {
    if (!isPartMainRow(p)) continue;
    const id = String(p._id);
    if (!id || id === excludeOut) continue;
    const n = inputVariantsForMain(id, catalog).length;
    const suffix = n > 0 ? ` (${n} varyant)` : " (varyantsız)";
    byId.set(id, {
      value: id,
      label: `${partBaseLabel(p)}${suffix}`,
    });
  }

  if (selectedId && !byId.has(selectedId)) {
    const direct = catalog.find((p) => String(p._id) === selectedId);
    const mainDoc =
      direct && isPartMainRow(direct)
        ? direct
        : catalog.find((p) => isPartMainRow(p) && String(p._id) === selectedId);
    if (mainDoc) {
      const id = String(mainDoc._id);
      const n = inputVariantsForMain(id, catalog).length;
      const suffix = n > 0 ? ` (${n} varyant)` : " (varyantsız)";
      byId.set(id, {
        value: id,
        label: `${partBaseLabel(mainDoc)}${suffix}`,
      });
    } else if (direct) {
      byId.set(selectedId, {
        value: selectedId,
        label: partInputLabel(direct),
      });
    }
  }

  return [...byId.values()].sort((a, b) =>
    a.label.localeCompare(b.label, "tr"),
  );
};

export const inferInputMainPartId = (inputMaterials, catalog) => {
  const first = (inputMaterials || []).find((m) => m?.partId);
  if (!first) return "";
  const doc = (catalog || []).find(
    (p) => String(p._id) === String(first.partId),
  );
  return doc ? partMainId(doc) : "";
};

export const syncInputMaterialsFromMain = (
  selectedOutputPartIds,
  inputMainPartId,
  existing = [],
  catalog = [],
) => {
  const selected = [
    ...new Set((selectedOutputPartIds || []).map(String).filter(Boolean)),
  ];
  if (!inputMainPartId || !selected.length) return [];

  const inputMid = String(inputMainPartId);
  const inputVariants = inputVariantsForMain(inputMid, catalog);
  const inputByMatchKey = new Map();
  for (const iv of inputVariants) {
    const key = variantMatchKeyOf(iv);
    if (key) inputByMatchKey.set(key, String(iv._id));
  }

  const ratioByInputPart = new Map();
  for (const m of existing || []) {
    if (m?.partId) {
      ratioByInputPart.set(String(m.partId), String(m.consumptionRatio ?? 1));
    }
  }

  return selected.flatMap((outId) => {
    const outDoc = partDocById(outId, [], catalog);
    const outKey = variantMatchKeyOf(outDoc);
    let partId = "";
    if (!outKey) {
      if (inputVariants.length === 0) partId = inputMid;
    } else {
      partId = inputByMatchKey.get(outKey) || "";
    }
    if (!partId) return [];
    return [
      {
        outputPartId: outId,
        partId,
        consumptionRatio: ratioByInputPart.get(partId) ?? "1",
      },
    ];
  });
};

export const getInputMatchState = (row, catalog = []) => {
  const selectedList = (row.selectedPartIds || []).map(String).filter(Boolean);
  if (!selectedList.length) {
    return { inputMats: [], unmatchedOutputs: [], missingInputMain: false };
  }
  if (!row.inputMainPartId) {
    return {
      inputMats: [],
      unmatchedOutputs: selectedList,
      missingInputMain: true,
    };
  }
  const inputMats = syncInputMaterialsFromMain(
    selectedList,
    row.inputMainPartId,
    row.inputMaterials,
    catalog,
  );
  const matchedOutputIds = new Set(
    inputMats.map((m) => String(m.outputPartId)),
  );
  const unmatchedOutputs = selectedList.filter(
    (outId) => !matchedOutputIds.has(String(outId)),
  );
  return { inputMats, unmatchedOutputs, missingInputMain: false };
};

export const formatUnmatchedOutputLabels = (outputIds, variantItems, catalog) =>
  outputIds
    .map((outId) => partInputLabel(partDocById(outId, variantItems, catalog)))
    .join(", ");

export const defaultMeasurementAxis = (index = 0) => ({
  payloadKey: `r${index}`,
  expected: "",
  tolerance: "",
  operator: "eq",
});

export const defaultOutputSettings = () => ({
  isFinalProduct: false,
  deviceModel: "",
  cycleQuantity: "1",
  netCycleTime: "",
  wastage: "0",
  productTypeVariable: "",
  measurementAxes: [defaultMeasurementAxis()],
});

export const getOutputSettings = (row, partId) => {
  const id = String(partId);
  return {
    ...defaultOutputSettings(),
    ...(row.outputSettingsByPartId?.[id] || {}),
  };
};

export const settingsFromPartDoc = (doc, record) => {
  // Convert legacy or new format to measurementAxes[]
  let measurementAxes;
  if (Array.isArray(record?.measurementAxes) && record.measurementAxes.length > 0) {
    measurementAxes = record.measurementAxes.map((a) => ({
      payloadKey: String(a.payloadKey ?? ""),
      expected: a.expected != null ? String(a.expected) : "",
      tolerance: a.tolerance != null ? String(a.tolerance) : "",
      operator: a.operator || "eq",
    }));
  } else {
    // Build from legacy width/height fields — tolerance stored as %
    const axes = [];
    const legacyPct = record?.tolerance != null ? Number(record.tolerance) : null;
    const wv = String(record?.widthVariable ?? "").trim();
    const wExp = record?.width != null && record?.width !== "" ? Number(record.width) : null;
    if (wv || (wExp != null && wExp > 0)) {
      const wTolAbs = record?.widthTolerance != null ? Number(record.widthTolerance) : null;
      // Convert absolute back to % if available, otherwise use legacy %
      const tolPct = wTolAbs != null && Number.isFinite(wTolAbs) && wExp
        ? (wTolAbs / Math.abs(wExp)) * 100
        : (legacyPct != null && Number.isFinite(legacyPct) ? legacyPct : null);
      axes.push({
        payloadKey: wv,
        expected: wExp != null ? String(wExp) : "",
        tolerance: tolPct != null && Number.isFinite(tolPct) ? String(Math.round(tolPct * 100) / 100) : "",
        operator: "eq",
      });
    }
    const hv = String(record?.heightVariable ?? "").trim();
    const hExp = record?.length != null && record?.length !== "" ? Number(record.length) : null;
    if (hv || (hExp != null && hExp > 0)) {
      const hTolAbs = record?.heightTolerance != null ? Number(record.heightTolerance) : null;
      const tolPct = hTolAbs != null && Number.isFinite(hTolAbs) && hExp
        ? (hTolAbs / Math.abs(hExp)) * 100
        : (legacyPct != null && Number.isFinite(legacyPct) ? legacyPct : null);
      axes.push({
        payloadKey: hv,
        expected: hExp != null ? String(hExp) : "",
        tolerance: tolPct != null && Number.isFinite(tolPct) ? String(Math.round(tolPct * 100) / 100) : "",
        operator: "eq",
      });
    }
    measurementAxes = axes.length > 0 ? axes : [defaultMeasurementAxis()];
  }

  return {
    isFinalProduct: !!doc?.isFinalProduct,
    deviceModel: doc?.isFinalProduct
      ? String(doc?.deviceModel?._id || doc?.deviceModel || "")
      : "",
    cycleQuantity:
      record?.cycleQuantity !== undefined ? String(record.cycleQuantity) : "1",
    netCycleTime:
      record?.netCycleTime !== undefined ? String(record.netCycleTime) : "",
    wastage:
      record?.wastage !== undefined && record?.wastage !== null
        ? String(record.wastage)
        : "0",
    productTypeVariable:
      record?.productTypeVariable !== undefined &&
      record?.productTypeVariable !== null
        ? String(record.productTypeVariable)
        : "",
    measurementAxes,
    outputWeight:
      record?.outputWeight != null && record?.outputWeight !== ""
        ? String(record.outputWeight)
        : "",
    weightUnit: record?.weightUnit ? String(record.weightUnit) : "",
  };
};

export const ensureOutputSettingsForVariants = (output) => {
  const next = { ...(output.outputSettingsByPartId || {}) };
  for (const partId of output.selectedPartIds || []) {
    const id = String(partId);
    next[id] = { ...defaultOutputSettings(), ...(next[id] || {}) };
  }
  return next;
};

export const getMeasurementAxesValidation = (settings) => {
  const axes = settings?.measurementAxes || [];
  const issues = [];
  for (let i = 0; i < axes.length; i++) {
    const ax = axes[i];
    const expectedText = String(ax?.expected ?? "").trim();
    const expectedNum = expectedText ? Number(expectedText) : 0;
    const toleranceText = String(ax?.tolerance ?? "").trim();
    const toleranceNum = toleranceText ? Number(toleranceText) : null;
    const payloadKey = String(ax?.payloadKey ?? "").trim();
    const active = Number.isFinite(expectedNum) && expectedNum > 0;
    if (expectedText && (Number.isNaN(expectedNum) || expectedNum < 0)) {
      issues.push({ idx: i, message: `Eksen ${i + 1}: beklenen değer >= 0 olmalı` });
      continue;
    }
    if (active && !payloadKey) {
      issues.push({ idx: i, message: `Eksen ${i + 1}: beklenen > 0 iken PLC değişkeni gerekli` });
    }
    if (active && toleranceText !== "" && (Number.isNaN(toleranceNum) || toleranceNum < 0)) {
      issues.push({ idx: i, message: `Eksen ${i + 1}: tolerans 0 veya daha büyük olmalı` });
    }
    if (toleranceText && !active && (Number.isNaN(toleranceNum) || toleranceNum < 0)) {
      issues.push({ idx: i, message: `Eksen ${i + 1}: tolerans >= 0 olmalı` });
    }
  }
  return { axes, issues, hasActiveAxis: axes.some((a) => Number(a?.expected) > 0) };
};

export const isVariantSettingsComplete = (output, partId) => {
  const s = getOutputSettings(output, partId);
  const cq = Number(s.cycleQuantity);
  if (!String(s.cycleQuantity ?? "").trim() || Number.isNaN(cq) || cq < 1) {
    return false;
  }
  const nt = Number(s.netCycleTime);
  if (!String(s.netCycleTime ?? "").trim() || Number.isNaN(nt) || nt <= 1) {
    return false;
  }
  if (s.isFinalProduct && !String(s.deviceModel || "").trim()) {
    return false;
  }
  const { issues } = getMeasurementAxesValidation(s);
  if (issues.length > 0) return false;
  return true;
};

export const collectVariantSettingsIssues = (output, catalog = []) => {
  const issues = [];
  const selected = (output?.selectedPartIds || []).map(String).filter(Boolean);
  if (!selected.length) {
    issues.push({ message: "Önce çıktı varyantı seçin (3. adım)" });
    return issues;
  }
  for (const partId of selected) {
    const doc = partDocById(partId, output.variantItems || [], catalog);
    const label = doc ? partInputLabel(doc) : partId;
    const s = getOutputSettings(output, partId);
    const cq = Number(s.cycleQuantity);
    if (!String(s.cycleQuantity ?? "").trim() || Number.isNaN(cq) || cq < 0) {
      issues.push({
        message: `${label}: geçerli çevrim miktarı girin`,
      });
    }
    const nt = Number(s.netCycleTime);
    if (!String(s.netCycleTime ?? "").trim() || Number.isNaN(nt) || nt <= 1) {
      issues.push({
        message: `${label}: net çevrim süresi 1 sn'den büyük olmalı`,
      });
    }
    if (s.isFinalProduct && !String(s.deviceModel || "").trim()) {
      issues.push({
        message: `${label}: nihai ürün için cihaz modeli seçin`,
      });
    }
    const axesVal = getMeasurementAxesValidation(s);
    for (const axIssue of axesVal.issues) {
      issues.push({ message: `${label}: ${axIssue.message}` });
    }
  }
  return issues;
};

export const buildInputMaterialsForSave = (row, outputPartId, catalog = []) => {
  const synced = syncInputMaterialsFromMain(
    row.selectedPartIds,
    row.inputMainPartId,
    row.inputMaterials,
    catalog,
  );
  const mat = synced.find(
    (m) => String(m.outputPartId) === String(outputPartId) && m.partId,
  );
  if (!mat) return [];
  return [
    {
      partId: mat.partId,
      consumptionRatio: Math.max(0, Number(mat.consumptionRatio) || 1),
    },
  ];
};

/** One input material (step 2 main; step 3 variants). */
export const emptyInputEntry = () => ({
  inputMainPartId: "",
  selectedPartIds: [],
  allowedPartIds: [],
  partId: "",
  consumptionRatio: "1",
  consumptionRatioByPartId: {},
  variantPage: 1,
  variantItems: [],
  variantTotal: 0,
  variantTotalPages: 1,
  variantLoading: false,
});

export const getEntryConsumptionRatio = (entry, partId) => {
  const pid = String(partId || "");
  const byPart = entry?.consumptionRatioByPartId || {};
  if (pid && byPart[pid] !== undefined && String(byPart[pid]).trim() !== "") {
    return String(byPart[pid]);
  }
  return String(entry?.consumptionRatio ?? "1");
};

export const formatEntryConsumptionSummary = (
  entry,
  catalog = [],
  mainPartById,
) => {
  const defaultRatio = String(entry?.consumptionRatio ?? "1");
  const allowed = resolveEntryAllowedPartIds(entry, catalog, mainPartById);
  const mp = mainPartById?.(entry?.inputMainPartId);
  const unit = partUnitLabel(mp);
  const unitSuffix = unit ? ` ${unit}` : "";
  if (!allowed.length) return `Kullanılan: ${defaultRatio}${unitSuffix}`;
  const ratios = [
    ...new Set(allowed.map((id) => getEntryConsumptionRatio(entry, id))),
  ];
  if (ratios.length === 1) return `Kullanılan: ${ratios[0]}${unitSuffix}`;
  return `Kullanılan: ${ratios.join(", ")}${unitSuffix} (varyanta göre)`;
};

/** Hammadde karşılık özeti (girdi satırı). */
export const formatInputEquivalentHint = (mainPartDoc) => {
  const meta = partEquivalentPerUnit(mainPartDoc);
  if (!meta) return "";
  const primary = partUnitLabel(mainPartDoc);
  const u = meta.unitSymbol || "—";
  return primary
    ? `1 ${primary} = ${meta.valuePerUnit} ${u}`
    : `1 birim = ${meta.valuePerUnit} ${u}`;
};

export const syncEntryConsumptionRatios = (
  entry,
  allowedPartIds,
  defaultRatio,
) => {
  const allowed = (allowedPartIds || []).map(String).filter(Boolean);
  const fallback = String(defaultRatio ?? entry?.consumptionRatio ?? "1");
  const prev = { ...(entry?.consumptionRatioByPartId || {}) };
  const next = {};
  for (const id of allowed) {
    next[id] = prev[id] !== undefined ? String(prev[id]) : fallback;
  }
  return next;
};

/** Set default miktar on a girdi row — overwrites all allowed variant ratios. */
export const mergeDefaultEntryConsumption = (
  entry,
  ratio,
  catalog = [],
  mainPartById,
  allowedPartIds,
) => {
  const ratioStr = String(ratio ?? "1");
  const allowed = (allowedPartIds || []).map(String).filter(Boolean);
  const resolved = allowed.length
    ? allowed
    : resolveEntryAllowedPartIds(entry, catalog, mainPartById);
  return {
    consumptionRatio: ratioStr,
    consumptionRatioByPartId: Object.fromEntries(
      resolved.map((id) => [id, ratioStr]),
    ),
  };
};

export const inputMainConfigured = (entry) => !!entry?.inputMainPartId;

export const resolveInputPartIds = (entry, mainPartById) => {
  const selected = (entry?.selectedPartIds || []).map(String).filter(Boolean);
  if (selected.length) return selected;
  if (entry?.partId) return [String(entry.partId)];
  const mp = mainPartById?.(entry?.inputMainPartId);
  if (mp && Number(mp.variantCount) === 0 && entry?.inputMainPartId) {
    return [String(entry.inputMainPartId)];
  }
  return [];
};

/** Effective input variants allowed for drag-drop / validation. */
export const resolveEntryAllowedPartIds = (
  entry,
  catalog = [],
  mainPartById,
) => {
  const explicit = (entry?.allowedPartIds || []).map(String).filter(Boolean);
  if (explicit.length) return explicit;

  if (!entry?.inputMainPartId) return [];

  const mp = mainPartById?.(entry.inputMainPartId);
  const variantCount = Number(mp?.variantCount) || 0;
  if (variantCount <= 0) return [String(entry.inputMainPartId)];

  const fromSelection = resolveInputPartIds(entry, mainPartById);
  if (fromSelection.length) return fromSelection;

  return inputVariantsForMain(entry.inputMainPartId, catalog)
    .map((v) => String(v._id))
    .filter(Boolean);
};

export const entryAllowedVariantsComplete = (entry, catalog, mainPartById) => {
  if (!entry?.inputMainPartId) return false;
  const mp = mainPartById?.(entry.inputMainPartId);
  const variantCount = Number(mp?.variantCount) || 0;
  if (variantCount <= 0) return true;
  return resolveEntryAllowedPartIds(entry, catalog, mainPartById).length > 0;
};

/** @deprecated use entryAllowedVariantsComplete */
export const inputVariantsComplete = (entry, mainPartById, catalog = []) =>
  entryAllowedVariantsComplete(entry, catalog, mainPartById);

export const emptyOutputRow = () => ({
  baseKey: "",
  selectedPartIds: [],
  excludedPartIds: [],
  recordIdsByPartId: {},
  variantPage: 1,
  variantItems: [],
  variantTotal: 0,
  variantTotalPages: 1,
  variantLoading: false,
  cycleQuantity: "1",
  netCycleTime: "",
  outputSettingsByPartId: {},
  manualInputAssignments: {},
  isActive: true,
});

/** @deprecated use emptyOutputRow — kept for groupRecordsToPartRows */
export const emptyPartRow = () => ({
  ...emptyOutputRow(),
  inputMainPartId: "",
  inputMaterials: [],
});

export const emptyMachineForm = () => ({
  machine: "",
  inputs: [emptyInputEntry()],
  output: emptyOutputRow(),
});

/** Load API records into machine + inputs[] + single output. */
export const recordsToMachineForm = (partRecords, partsCatalog = []) => {
  const outputRows = groupRecordsToPartRows(partRecords, partsCatalog);
  const output = outputRows.length
    ? {
        ...outputRows[0],
        inputMainPartId: undefined,
        inputMaterials: undefined,
      }
    : emptyOutputRow();

  const inputByPartId = new Map();
  for (const item of partRecords) {
    for (const m of item.inputMaterials || []) {
      const pid = String(m.partId?._id || m.partId || "");
      if (!pid || inputByPartId.has(pid)) continue;
      const doc = (partsCatalog || []).find((p) => String(p._id) === pid);
      const inputMainPartId = doc
        ? partMainId(doc)
        : inferInputMainPartId([{ partId: pid }], partsCatalog);
      inputByPartId.set(pid, {
        inputMainPartId: String(inputMainPartId || ""),
        partId: pid,
        selectedPartIds: [pid],
        consumptionRatio: String(m.consumptionRatio ?? 1),
      });
    }
  }

  const inputs = inputByPartId.size
    ? [...inputByPartId.values()]
    : [emptyInputEntry()];

  return { inputs, output };
};

export const inputEntryConfigured = (entry, mainPartById) =>
  resolveInputPartIds(entry, mainPartById).length > 0;

/** Input variant part ids that correspond to selected output variants (by variantId). */
export const syncInputSelectedPartIdsForOutputs = (
  selectedOutputPartIds,
  inputMainPartId,
  catalog = [],
) => {
  const selected = [
    ...new Set((selectedOutputPartIds || []).map(String).filter(Boolean)),
  ];
  if (!inputMainPartId || !selected.length) return [];

  const inputMid = String(inputMainPartId);
  const inputVariants = inputVariantsForMain(inputMid, catalog);
  if (inputVariants.length === 0) return [inputMid];

  const inputByMatchKey = new Map();
  for (const iv of inputVariants) {
    const key = variantMatchKeyOf(iv);
    if (key) inputByMatchKey.set(key, String(iv._id));
  }

  const matched = new Set();
  for (const outId of selected) {
    const outDoc = partDocById(outId, [], catalog);
    const outKey = variantMatchKeyOf(outDoc);
    if (!outKey) continue;
    const inId = inputByMatchKey.get(outKey);
    if (inId) matched.add(inId);
  }
  return [...matched];
};

/** Manual assignments: one allowed input part per girdi per output (by variantId). */
export const buildManualAssignmentsByVariantMatch = (
  outputPartIds = [],
  inputs = [],
  catalog = [],
  mainPartById,
  variantItems = [],
) => {
  const outputIds = outputPartIds.map(String).filter(Boolean);
  const assignments = {};
  const unmatched = [];

  for (const outId of outputIds) {
    const outDoc = partDocById(outId, variantItems, catalog);
    const outLabel = outDoc ? partInputLabel(outDoc) : outId;
    const outCode = outDoc?.code || "—";
    const outMatchKey = variantMatchKeyOf(outDoc);
    const entryAssignments = [];
    const issues = [];

    for (let i = 0; i < inputs.length; i++) {
      const entry = inputs[i];
      if (!entry?.inputMainPartId) continue;

      const inputMid = String(entry.inputMainPartId);
      const mp = mainPartById?.(entry.inputMainPartId);
      const entryLabel = mp ? partBaseLabel(mp) : `Girdi ${i + 1}`;
      const allowedSet = new Set(
        resolveEntryAllowedPartIds(entry, catalog, mainPartById),
      );
      const inputVariants = inputVariantsForMain(inputMid, catalog);

      if (inputVariants.length === 0) {
        if (!allowedSet.has(inputMid)) {
          issues.push(`${entryLabel}: izinli girdi listesinde yok`);
          continue;
        }
        entryAssignments.push({
          partId: inputMid,
          entryIdx: i,
          consumptionRatio: getEntryConsumptionRatio(entry, inputMid),
        });
        continue;
      }

      if (!outMatchKey) {
        issues.push(
          `${entryLabel}: çıktı "${outCode}" için eşleşme anahtarı yok (variantId/kod)`,
        );
        continue;
      }

      const inputByMatchKey = new Map();
      for (const iv of inputVariants) {
        const pid = String(iv._id);
        if (!allowedSet.has(pid)) continue;
        const key = variantMatchKeyOf(iv);
        if (key) inputByMatchKey.set(key, pid);
      }

      const pid = inputByMatchKey.get(outMatchKey);
      if (pid) {
        entryAssignments.push({
          partId: pid,
          entryIdx: i,
          consumptionRatio: getEntryConsumptionRatio(entry, pid),
        });
      } else {
        const allowedCodes = [...allowedSet]
          .map((id) => {
            const d = partDocById(id, [], catalog);
            return d
              ? `${d.code || partInputLabel(d)} (${variantMatchKeyOf(d) || "?"})`
              : id;
          })
          .filter(Boolean)
          .join(", ");
        issues.push(
          `${entryLabel}: çıktı "${outCode}" [${outMatchKey}] ↔ izinli girdi yok` +
            (allowedCodes ? ` — izinli: ${allowedCodes}` : ""),
        );
      }
    }

    assignments[outId] = entryAssignments;
    if (!entryAssignments.length && issues.length) {
      unmatched.push({ outputId: outId, outputLabel: outLabel, issues });
    } else if (issues.length) {
      unmatched.push({
        outputId: outId,
        outputLabel: outLabel,
        issues: [`Kısmi: ${issues.join("; ")}`],
      });
    }
  }

  const totalAssigned = Object.values(assignments).reduce(
    (n, list) => n + (list?.length || 0),
    0,
  );

  return { assignments, unmatched, totalAssigned };
};

export const formatAutoAssignUnmatchedMessage = (unmatched = []) => {
  if (!unmatched.length) return "";
  const byReason = new Map();
  for (const { outputLabel, issues } of unmatched) {
    const reason = issues.join("; ");
    if (!byReason.has(reason)) byReason.set(reason, []);
    byReason.get(reason).push(outputLabel);
  }
  const lines = [...byReason.entries()].map(([reason, outputs]) => {
    const names = [...new Set(outputs)];
    const shown = names.slice(0, 5).join(", ");
    const extra = names.length > 5 ? ` +${names.length - 5} çıktı` : "";
    return `${reason} → ${shown}${extra}`;
  });
  const shown = lines.slice(0, 3);
  const tail =
    lines.length > shown.length
      ? ` (+${lines.length - shown.length} sebep)`
      : "";
  return `${shown.join(" | ")}${tail}`;
};

export const syncInputEntriesFromOutput = (
  inputs = [],
  selectedOutputPartIds,
  catalog = [],
) =>
  (inputs || []).map((entry) => {
    const synced = syncInputSelectedPartIdsForOutputs(
      selectedOutputPartIds,
      entry.inputMainPartId,
      catalog,
    );
    return {
      ...entry,
      selectedPartIds: synced,
      partId: synced[0] ? String(synced[0]) : "",
    };
  });

/** Match state for one girdi row against current output selection. */
export const getEntryMatchState = (entry, output, catalog = []) => {
  if (!entry?.inputMainPartId) {
    const outputIds = (output?.selectedPartIds || [])
      .map(String)
      .filter(Boolean);
    return {
      inputMats: [],
      unmatchedOutputs: outputIds,
      missingInputMain: true,
    };
  }
  return getInputMatchState(
    {
      inputMainPartId: entry.inputMainPartId,
      selectedPartIds: output?.selectedPartIds || [],
      inputMaterials: [],
    },
    catalog,
  );
};

/** Detailed validation issues (sidebar-style). */
export const collectAssignmentValidationIssues = (
  output,
  inputs = [],
  catalog = [],
  mainPartById,
) => {
  const issues = [];

  if (!output?.baseKey) {
    issues.push({ kind: "output", message: "Çıktı ana parça seçilmedi" });
    return issues;
  }

  const outputIds = (output.selectedPartIds || []).map(String).filter(Boolean);
  const mpOut = mainPartById?.(output.baseKey);
  const outputVariantCount = Number(mpOut?.variantCount) || 0;
  if (outputVariantCount > 0 && !outputIds.length) {
    issues.push({ kind: "output", message: "En az bir çıktı varyantı seçin" });
  }

  if (!inputs?.length) {
    issues.push({ kind: "global", message: "En az bir girdi ekleyin" });
    return issues;
  }

  inputs.forEach((entry, i) => {
    if (!inputMainConfigured(entry)) {
      issues.push({
        kind: "entry",
        entryIdx: i,
        message: `Girdi ${i + 1}: ana parça ve miktar girin`,
      });
    }
  });

  if (!outputIds.length) return issues;

  const assignments = output.manualInputAssignments || {};
  const unassigned = outputIds.filter((id) => !(assignments[id] || []).length);
  if (unassigned.length) {
    issues.push({
      kind: "output",
      message: `Atanmamış çıktı varyantları: ${formatUnmatchedOutputLabels(
        unassigned,
        output.variantItems,
        catalog,
      )}`,
      unassignedOutputs: unassigned,
    });
  }

  return issues;
};

export const assignmentIsValid = (
  output,
  inputs = [],
  catalog = [],
  mainPartById,
) =>
  collectAssignmentValidationIssues(output, inputs, catalog, mainPartById)
    .length === 0;

export const allInputsMatchOutputs = (output, inputs = [], catalog = []) => {
  const outputIds = (output?.selectedPartIds || []).map(String).filter(Boolean);
  if (!outputIds.length) return false;
  for (const outId of outputIds) {
    for (const entry of inputs) {
      if (!entry?.inputMainPartId) return false;
      const mats = syncInputMaterialsFromMain(
        [outId],
        entry.inputMainPartId,
        [],
        catalog,
      );
      if (!mats.length) return false;
    }
  }
  return true;
};

export const buildInputMaterialsForOutput = (
  outputPartId,
  inputs = [],
  catalog = [],
) =>
  (inputs || []).flatMap((entry) => {
    if (!entry?.inputMainPartId) return [];
    const mats = syncInputMaterialsFromMain(
      [String(outputPartId)],
      entry.inputMainPartId,
      [],
      catalog,
    );
    return mats.map((m) => ({
      partId: String(m.partId),
      consumptionRatio: Math.max(
        0,
        Number(getEntryConsumptionRatio(entry, m.partId)) || 1,
      ),
    }));
  });

const findEntryIdxForInputPart = (
  inputPartId,
  inputs = [],
  catalog = [],
  mainPartById,
) => {
  const pid = String(inputPartId || "");
  if (!pid) return -1;
  const doc = (catalog || []).find((p) => String(p._id) === pid);
  const inputMainId = doc ? partMainId(doc) : "";
  if (inputMainId) {
    const idx = inputs.findIndex(
      (e) => String(e.inputMainPartId) === String(inputMainId),
    );
    if (idx >= 0) return idx;
  }
  for (let i = 0; i < inputs.length; i++) {
    const allowed = resolveEntryAllowedPartIds(
      inputs[i],
      catalog,
      mainPartById,
    );
    if (allowed.includes(pid)) return i;
  }
  return inputs.length > 0 ? 0 : -1;
};

/** Restore step-3 manual assignments from saved PartMachine rows. */
export const buildManualAssignmentsFromRecords = (
  partRecords = [],
  inputs = [],
  catalog = [],
  mainPartById,
) => {
  const assignments = {};
  for (const item of partRecords) {
    const outId = String(item.part?._id || item.part || "");
    if (!outId) continue;
    const list = [];
    for (const m of item.inputMaterials || []) {
      const inputPartId = String(m.partId?._id || m.partId || "");
      if (!inputPartId) continue;
      const entryIdx = findEntryIdxForInputPart(
        inputPartId,
        inputs,
        catalog,
        mainPartById,
      );
      if (entryIdx < 0) continue;
      list.push({
        partId: inputPartId,
        entryIdx,
        consumptionRatio: String(m.consumptionRatio ?? 1),
      });
    }
    if (list.length) assignments[outId] = list;
  }
  return assignments;
};

/** Label for one manual girdi assignment (view / summary). */
export const formatAssignedInputLabel = (
  assignment,
  inputs = [],
  catalog = [],
) => {
  if (!assignment?.partId) return "—";
  const doc = partDocById(assignment.partId, [], catalog);
  if (doc) {
    const code = doc.code || "";
    const v = partVariantLabel(doc);
    if (code && v && v !== "—") return `${code} (${v})`;
    return code || partInputLabel(doc);
  }
  const entry = inputs[assignment.entryIdx];
  return entry?.inputMainPartId
    ? `Girdi ${Number(assignment.entryIdx) + 1}`
    : String(assignment.partId);
};

export const getOutputAssignmentLabels = (
  outputPartId,
  manualAssignments = {},
  inputs = [],
  catalog = [],
) =>
  (manualAssignments[String(outputPartId)] || []).map((a) =>
    formatAssignedInputLabel(a, inputs, catalog),
  );

/** API payload: manual assignments first; legacy variant-sync only if empty. */
export const inputMaterialsForOutputVariant = (
  outputPartId,
  manualAssignments = {},
  inputs = [],
  catalog = [],
  mainPartById,
) => {
  const outId = String(outputPartId);
  const manual = manualAssignments[outId];
  if (manual?.length) {
    return manual.map(({ partId: inputPartId, entryIdx }) => ({
      partId: String(inputPartId),
      consumptionRatio: Math.max(
        0,
        Number(getEntryConsumptionRatio(inputs[entryIdx], inputPartId)) || 1,
      ),
    }));
  }
  return buildInputMaterialsForOutput(outId, inputs, catalog);
};

export const groupRecordsToPartRows = (partRecords, partsCatalog = []) => {
  const map = new Map();
  for (const item of partRecords) {
    const p = item.part || {};
    const key = partMainId(p);
    const partId = String(p._id || item.part || "");
    if (!key || !partId) continue;
    if (!map.has(key)) {
      map.set(key, {
        baseKey: key,
        selectedPartIds: [],
        recordIdsByPartId: {},
        inputMainPartId: "",
        cycleQuantity:
          item.cycleQuantity !== undefined ? String(item.cycleQuantity) : "1",
        netCycleTime:
          item.netCycleTime !== undefined ? String(item.netCycleTime) : "",
        inputMaterials: [],
        outputSettingsByPartId: {},
        isActive: item.isActive !== false,
      });
    }
    const row = map.get(key);
    if (!row.selectedPartIds.includes(partId)) {
      row.selectedPartIds.push(partId);
    }
    if (item._id) row.recordIdsByPartId[partId] = String(item._id);
    row.outputSettingsByPartId[partId] = settingsFromPartDoc(p, item);
    for (const m of item.inputMaterials || []) {
      const pid = String(m.partId?._id || m.partId || "");
      if (!pid) continue;
      row.inputMaterials.push({
        outputPartId: partId,
        partId: pid,
        consumptionRatio: String(m.consumptionRatio ?? 1),
      });
    }
  }
  return Array.from(map.values()).map((row) => {
    const inputMainPartId =
      row.inputMainPartId ||
      inferInputMainPartId(row.inputMaterials, partsCatalog);
    return {
      ...row,
      inputMainPartId,
      inputMaterials: syncInputMaterialsFromMain(
        row.selectedPartIds,
        inputMainPartId,
        row.inputMaterials,
        partsCatalog,
      ),
    };
  });
};

export const ASSIGNMENT_STEPS = [
  {
    id: 1,
    key: "machine",
    label: "Makine",
    hint: "Parça ataması yapılacak makineyi seçin.",
  },
  {
    id: 2,
    key: "entries",
    label: "Girdiler",
    hint: "Girdi ana parçalarını ekleyin; varyant seçimi 3. adımda yapılır.",
  },
  {
    id: 3,
    key: "output",
    label: "Çıktı",
    hint: "Girdi ataması ve çıktı başına fire (kompakt alan) bu adımda tanımlanır.",
  },
  {
    id: 4,
    key: "variantSettings",
    label: "Varyant ayarları",
    hint: "Her çıktı varyantı için çevrim ve nihai ürün bilgisini girin.",
  },
  {
    id: 5,
    key: "summary",
    label: "Özet",
    hint: "Tüm parça atamalarını gözden geçirin, durumu ayarlayın ve kaydedin.",
  },
];

export const emptyPartAssignment = () => ({
  id: Math.random().toString(36).slice(2),
  inputs: [emptyInputEntry()],
  output: emptyOutputRow(),
});

export const emptyMachineFormMulti = () => ({
  machine: "",
  parts: [emptyPartAssignment()],
});

export const recordsToMultiPartAssignments = (
  partRecords,
  partsCatalog = [],
) => {
  const outputGroups = groupRecordsToPartRows(partRecords, partsCatalog);
  if (!outputGroups.length) return [emptyPartAssignment()];

  return outputGroups.map((row) => {
    const groupRecords = partRecords.filter((item) => {
      const p = item.part || {};
      return partMainId(p) === row.baseKey;
    });

    const mainPartById = (id) =>
      (partsCatalog || []).find(
        (p) => String(p._id) === String(id) && isPartMainRow(p),
      ) || null;

    const inputByMainId = new Map();
    for (const item of groupRecords) {
      for (const m of item.inputMaterials || []) {
        const pid = String(m.partId?._id || m.partId || "");
        if (!pid) continue;
        const doc = (partsCatalog || []).find((p) => String(p._id) === pid);
        const inputMainId = doc
          ? partMainId(doc)
          : inferInputMainPartId([{ partId: pid }], partsCatalog);
        const mid = String(inputMainId || "");
        if (!mid) continue;

        const allowedForMain = inputVariantsForMain(mid, partsCatalog)
          .map((v) => String(v._id))
          .filter(Boolean);

        let entry = inputByMainId.get(mid);
        if (!entry) {
          entry = {
            inputMainPartId: mid,
            partId: pid,
            selectedPartIds: [],
            allowedPartIds: allowedForMain.length ? [...allowedForMain] : [pid],
            consumptionRatio: String(m.consumptionRatio ?? 1),
            consumptionRatioByPartId: {},
            variantPage: 1,
            variantItems: [],
            variantTotal: 0,
            variantTotalPages: 1,
            variantLoading: false,
          };
          inputByMainId.set(mid, entry);
        }

        if (!entry.consumptionRatioByPartId)
          entry.consumptionRatioByPartId = {};
        entry.consumptionRatioByPartId[pid] = String(m.consumptionRatio ?? 1);

        if (!entry.allowedPartIds.includes(pid)) {
          entry.allowedPartIds = [...entry.allowedPartIds, pid];
        }
        if (!entry.selectedPartIds.includes(pid)) {
          entry.selectedPartIds.push(pid);
        }
        entry.partId = entry.partId || pid;
      }
    }

    const inputs =
      inputByMainId.size > 0
        ? [...inputByMainId.values()]
        : [emptyInputEntry()];

    const syncedInputs = syncInputEntriesFromOutput(
      inputs,
      row.selectedPartIds,
      partsCatalog,
    );

    const manualInputAssignments = buildManualAssignmentsFromRecords(
      groupRecords,
      syncedInputs,
      partsCatalog,
      mainPartById,
    );

    const output = {
      baseKey: row.baseKey,
      selectedPartIds: row.selectedPartIds,
      excludedPartIds: [],
      recordIdsByPartId: row.recordIdsByPartId || {},
      variantPage: 1,
      variantItems: [],
      variantTotal: 0,
      variantTotalPages: 1,
      variantLoading: false,
      outputSettingsByPartId: row.outputSettingsByPartId || {},
      manualInputAssignments,
      isActive: row.isActive !== false,
    };

    return {
      id: row.baseKey,
      inputs: syncedInputs,
      output,
    };
  });
};
